import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Button, IconButton, Menu, MenuItem } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import Link from 'next/link';

interface Props {
  // Identifiant utilisé pour mémoriser la fermeture (par page).
  storageKey: string;
  title?: string;
  body?: string;
  onActivateNotifications?: () => void;
  notificationsHref?: string;
  showFavoritesCTA?: boolean;
  variant?: 'primary' | 'secondary';
}

const DISMISS_LATER_HOURS = 24;

const NotificationCTABanner: React.FC<Props> = ({
  storageKey,
  title = 'Activez les notifications pour ne rien rater',
  body = "Recevez par email ou dans l'application les nouveautés correspondant à vos critères.",
  onActivateNotifications,
  notificationsHref,
  showFavoritesCTA = false,
  variant = 'secondary',
}) => {
  const [visible, setVisible] = useState(false);
  const [closeAnchor, setCloseAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(`ouaaa_cta_${storageKey}`);
      if (!raw) {
        setVisible(true);
        return;
      }
      const data = JSON.parse(raw);
      if (data.until === 'never') return;
      if (data.until && Date.now() < data.until) return;
      setVisible(true);
    } catch {
      setVisible(true);
    }
  }, [storageKey]);

  const dismiss = (mode: 'later' | 'never') => {
    try {
      localStorage.setItem(
        `ouaaa_cta_${storageKey}`,
        JSON.stringify({
          until: mode === 'never' ? 'never' : Date.now() + DISMISS_LATER_HOURS * 3600 * 1000,
        }),
      );
    } catch {}
    setVisible(false);
    setCloseAnchor(null);
  };

  if (!visible) return null;

  const accent = variant === 'secondary' ? '#d96552' : '#2c367e';

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: variant === 'secondary' ? 'rgba(217,101,82,0.08)' : 'rgba(44,54,126,0.06)',
        borderTop: `1px solid ${accent}33`,
        borderBottom: `1px solid ${accent}33`,
        py: { xs: 0.5, sm: 1 },
        px: { xs: 1, md: 3 },
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 2 }}
        alignItems="center"
      >
        <NotificationsActiveIcon
          sx={{ color: accent, flexShrink: 0, fontSize: { xs: 18, sm: 24 } }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: '#2c367e',
              fontWeight: 600,
              lineHeight: 1.2,
              fontSize: { xs: '0.78rem', sm: '0.875rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: { xs: 'none', sm: 'block' },
              lineHeight: 1.3,
            }}
          >
            {body}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {onActivateNotifications ? (
            <Button
              onClick={onActivateNotifications}
              variant="text"
              size="small"
              startIcon={<NotificationsActiveIcon fontSize="small" />}
              sx={{
                color: accent,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                minWidth: 0,
                px: { xs: 0.5, sm: 1 },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
                '& .MuiButton-startIcon > *:first-of-type': { fontSize: { xs: 18, sm: 20 } },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Activer
              </Box>
            </Button>
          ) : (
            notificationsHref && (
              <Button
                component={Link as any}
                href={notificationsHref}
                variant="text"
                size="small"
                startIcon={<NotificationsActiveIcon fontSize="small" />}
                sx={{
                  color: accent,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  px: { xs: 0.5, sm: 1 },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Activer
                </Box>
              </Button>
            )
          )}
          {showFavoritesCTA && (
            <Button
              component={Link as any}
              href="/account/filters"
              variant="text"
              size="small"
              startIcon={<StarIcon fontSize="small" />}
              sx={{ color: accent, whiteSpace: 'nowrap' }}
            >
              Favoris
            </Button>
          )}
          <IconButton
            size="small"
            aria-label="Fermer le bandeau"
            onClick={(e) => setCloseAnchor(e.currentTarget)}
            sx={{ p: { xs: 0.25, sm: 0.5 } }}
          >
            <CloseIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          </IconButton>
          <Menu
            anchorEl={closeAnchor}
            open={Boolean(closeAnchor)}
            onClose={() => setCloseAnchor(null)}
          >
            <MenuItem onClick={() => dismiss('later')}>Plus tard (24h)</MenuItem>
            <MenuItem onClick={() => dismiss('never')}>Ne plus afficher</MenuItem>
          </Menu>
        </Stack>
      </Stack>
    </Box>
  );
};

export default NotificationCTABanner;
